import {
  useBreakpointIndex,
  Box,
  ThemeUICSSObject,
  getVariant,
} from '@apia/theme';
import React, { useState } from 'react';
import usePortal from 'react-cool-portal';
import { CSSTransition } from 'react-transition-group';
import { useMenu } from '../menuController';
import { IconButton } from '../../components';

/**
 * Este aside solamente aparece en el breakpoint 3 y es el que
 * se muestra en el menú de hamburguesa.
 */

export const HamburguerMenu = ({
  container = '.TitleContainer',
  header = 'MainHeader',
  forceVisibility,
}: {
  container?: string;
  forceVisibility?: boolean;
  header?: string;
}) => {
  const [hamburguerRef, setHamburguerRef] = React.useState<HTMLElement | null>(
    null,
  );
  const [menuRef, setMenuRef] = React.useState<HTMLElement | null>(null);

  const [menuCoords, setMenuCoords] = React.useState<
    ThemeUICSSObject | undefined
  >(undefined);
  const [hamburguerCoords, setHamburguerCoords] =
    React.useState<ThemeUICSSObject | null>(null);

  const { Portal } = usePortal({
    containerId: 'NonControlledAppModals',
    autoRemoveContainer: false,
    defaultShow: true,
    clickOutsideToHide: false,
    escToHide: false,
  });

  React.useLayoutEffect(() => {
    const unsuscribers: (() => unknown)[] = [];
    if (hamburguerRef) {
      const resize = () => {
        const bounding = (
          document.querySelector(container) as HTMLElement
        ).getBoundingClientRect();

        if (!hamburguerRef || bounding.height === 0) return;

        const hamburguerHeight = 40;

        const hamburguerTop =
          bounding.top +
          bounding.height / 2 -
          hamburguerHeight / 2 +
          window.scrollY;

        const shouldOpenBelow = bounding.top <= window.innerHeight / 3;

        const menuTop = shouldOpenBelow
          ? hamburguerTop + hamburguerHeight + 10
          : 10;
        const menuRight = shouldOpenBelow ? 10 : 60;
        const maxHeight = shouldOpenBelow
          ? window.innerHeight - menuTop - 50
          : window.innerHeight - 50;

        setMenuCoords({
          right: `${menuRight}px`,
          top: `${menuTop}px`,
          maxHeight: `${maxHeight}px`,
          overflow: 'auto',
        });
        setHamburguerCoords({
          right: `${10}px`,
          top: `${hamburguerTop}px`,
        });
      };

      const observer = new ResizeObserver(resize);

      observer.observe(document.getElementById(header) as HTMLElement);

      unsuscribers.push(() => void observer.disconnect());

      resize();
    }

    return () => unsuscribers.forEach((unsuscribe) => unsuscribe());
  }, [container, hamburguerRef, header, menuRef]);

  const children = useMenu([4, 5, 6, 7]);

  const iconSx = React.useMemo(
    () =>
      hamburguerCoords
        ? ({
            position: 'fixed',
            zIndex: 'menu',
            ...hamburguerCoords,
          } as ThemeUICSSObject)
        : undefined,
    [hamburguerCoords],
  );

  const menuSx = React.useMemo(
    () =>
      menuCoords
        ? ({
            position: 'fixed',
            zIndex: 'menu',
            ...menuCoords,
          } as ThemeUICSSObject)
        : undefined,
    [menuCoords],
  );

  const breakpoint = useBreakpointIndex({ defaultIndex: 5 });
  const [isVisible, setIsVisible] = useState(false);

  return (
    <>
      <Box
        id="HamburguerIconWrapper"
        className={`HamburguerIconWrapper ${forceVisibility ? 'forced' : ''}`}
        ref={setHamburguerRef}
        as="aside"
        aria-label={window.LBL_OPTIONS_VAR}
      >
        <IconButton
          sx={iconSx}
          icon={'Hamburguer'}
          className="hamburguerIcon"
          onClick={() => setIsVisible((s) => !s)}
          aria-label={window.LBL_OPEN_MENU}
        />
      </Box>
      <Portal>
        <Box>
          <CSSTransition
            in={isVisible}
            timeout={150}
            classNames="hamburguerMenu"
          >
            <Box
              ref={setMenuRef}
              {...getVariant('layout.twoColumnsAside.hamburguerMenu')}
              className="hamburguerMenu"
              sx={menuSx}
              as="aside"
              aria-label={window.LBL_OPTIONS_AND_TABS}
            >
              {(breakpoint === 4 || forceVisibility) && children}
            </Box>
          </CSSTransition>
        </Box>
      </Portal>
    </>
  );
};
